import React, { Component } from 'react';
import Overlay from "../Overlay";
import SimpleTable from "../tables/SimpleTable";
import { ReactSelect } from "../FilterCard";
import {
  getLabel,
  getNumPages,
  getPageOptions,
  hideModal,
  showModalNoOutsideClick,
  slicePages,
} from "../../util/FormatUtil";
import { sweetalert } from "../../App";
import Select from "react-select";
import ExemptionRecordModal from '../modals/ExemptionRecordModal';
import NetworkUtil from "../../network/NetworkUtil";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import PaginationTool from "../PaginationTool";
import SystemAPI from "../../network/SystemAPI";
import ExemptionRecordsFilterTabs from './ExemptionRecordsFilterTabs';
import ExemptionAPI from '../../network/ExemptionAPI';
import VaccineExemptionCertificate from '../resultReports/VaccineExemptionCertificate';
import moment from 'moment';
import AdminAPI from '../../network/AdminAPI';
import UsersAPI from '../../network/UsersAPI';
import { AuthContext } from '../../context/AuthContext';
import { getIdToken } from 'firebase/auth';
import { ReportSelectModal } from '../modals/ReportSelectModal';
import { ExemptionExportsModal } from '../modals/ExemptionExportsModal';
import IDVerificationUpload from './IDVerificationUpload';
import IDVerificationPending from './IDVerificationPending';
import IDVerificationDenied from './IDVerificationDenied';

const ITEMS_PER_PAGE = 25;

interface ExemptionRecordsState {
  showLoading: boolean;
  selectedExemption;
  exemptionQueryResults;
  tableData: {}[];
  filter?
  direction?: TableOrder;
  page_options: ReactSelect[];
  selected_page?: { label; value }
  filteredObj?
  totalExemptions?
  searched?
  immunizations?
  showCertificate?
  races?
  genders?
  ethnicities?
  branding?
  states?
  countries?
  exemptionTypes?
  districts?
  schools?
  schoolOptions?
  isAdmin?: boolean
  isSchoolUser?: boolean
  userFacilityIDs?: number[]
  userFacilities?: { id: number, name: string }[]
  openSearchMode?: boolean
  openSearchFirstName?: string
  openSearchLastName?: string
  openSearchDOB?: string
  grades?
  exemptionReasons?
  showLoadingResultReport?: boolean
  showLoadingForReportSelect?: boolean
  counties?
  dataAgreementAccepted?: boolean
  dataAgreementError?: boolean
  userUUID?: string
  idVerificationStatus?: string
  permissionsLoaded?: boolean
  hasSearched?: boolean
}

export default class ExemptionRecords extends Component<
  any,
  ExemptionRecordsState
> {
  static contextType = AuthContext;
  private exemptionFilterTabsRef: any;
  constructor(props) {
    super(props);

    this.state = {
      showLoading: false,
      selectedExemption: null,
      showLoadingResultReport: false,
      tableData: [] as any,
      exemptionQueryResults: [] as any,
      filteredObj: {},
      hasSearched: false,
      direction: "desc",
      page_options: [{ label: "1", value: "1" }],
      selected_page: { label: "1", value: 1 },
    };

    this.exemptionFilterTabsRef = React.createRef();
    this.viewPDFNewTab = this.viewPDFNewTab.bind(this);
  }

  componentDidMount(): void {
    document.title = 'Exemption Records | Oklahoma State Department of Health';
    AdminAPI.getPermissions().then(async (data) => {
      // School Users must complete onboarding steps before accessing exemption data
      if (data.isSchoolUser && (!data.dataAgreementAccepted || data.idVerificationStatus !== 'Approved')) {
        try {
          let idToken = await getIdToken(this.context, true);
          const userCredential = await AdminAPI.getAuthenticated({ token: idToken });

          if (!userCredential?.user?.UUID) {
            sweetalert.fire({
              icon: "error",
              text: "Unable to verify user credentials. Please try logging in again."
            });
            return;
          }

          this.setState({
            isSchoolUser: true,
            dataAgreementAccepted: !!data.dataAgreementAccepted,
            idVerificationStatus: data.idVerificationStatus || null,
            userUUID: userCredential.user.UUID,
            permissionsLoaded: true,
          });
          return;
        } catch (error) {
          sweetalert.fire({
            icon: "error",
            text: "An error occurred while verifying your credentials. Please try again later."
          });
          return;
        }
      }

      this.setState({ isAdmin: data.isAdmin, isSchoolUser: data.isSchoolUser, dataAgreementAccepted: true, idVerificationStatus: 'Approved', userFacilityIDs: data.userFacilityIDs || [], userFacilities: data.userFacilities || [], permissionsLoaded: true }, () => {
        this.loadInitialExemptions();
      });
    });
    SystemAPI.getAllGenders().then(data => {
      this.setState({ genders: data })
    })
    SystemAPI.getAllRaces().then(data => {
      this.setState({ races: data })
    })
    SystemAPI.getAllEthnicities().then(data => {
      this.setState({ ethnicities: data })
    })
    SystemAPI.getAllImmunizations().then(data => {
      this.setState({ immunizations: data })
    })
    SystemAPI.getProductBrandingFromURL(window.location.href).then(data => {
      this.setState({ branding: data })
    })
    SystemAPI.getAllStates().then(data => {
      this.setState({ states: data })
    })
    SystemAPI.getAllCountries().then(data => {
      this.setState({ countries: data })
    })
    SystemAPI.getAllExemptionTypes().then(data => {
      this.setState({ exemptionTypes: data })
    })
    SystemAPI.getAllSchoolDistricts().then(data => {
      this.setState({ districts: data })
    })
    // Source the School filter options from the Facility table (FacilityType = School),
    // matching what EducationSite.School actually stores (a Facility ID) and what the
    // create/edit forms and ExemptionRecordModal use. The legacy /schools endpoint
    // returned a SchoolID that is empty for these rows, which broke the filter.
    SystemAPI.getFacilitySchools().then((data: any) => {
      this.setState({ schools: data })
    })
    SystemAPI.getAllSchoolGrades().then(data => {
      this.setState({ grades: data.grades })
    })
    SystemAPI.getAllPersonalExemptionReasons().then(data => {
      this.setState({ exemptionReasons: data })
    })
    SystemAPI.getAllCounties().then(response => {
      let allCountyData = response.counties;
      if (allCountyData && allCountyData.length > 0) {
        allCountyData = allCountyData.map((county) => {
          return { label: county.County, value: county.State };
        });
      }
      this.setState({ counties: allCountyData });
    })

  }

  loadInitialExemptions() {
    // Admins use a search-first flow: don't auto-query on page load (avoids an
    // unfiltered count + fetch on every visit). The filters render and the user
    // runs a search to populate the table. School users are facility-scoped to a
    // small set, so load their (Approved) records immediately.
    if (!this.state.isSchoolUser) {
      this.setState({ showLoading: false, tableData: [], totalExemptions: 0, hasSearched: false });
      return;
    }
    const defaultStatus = [1];
    this.setState({ showLoading: true, filteredObj: { Status: defaultStatus }, hasSearched: true }, async () => {
      let results = await ExemptionAPI.getFilteredExemptions(1, {
        filter: {
          Status: defaultStatus
        },
      });
      if (results.success === false) {
        sweetalert.fire({
          icon: "error",
          title: "",
          text: results.reason || "Unable to load exemption records.",
        });
        this.setState({ showLoading: false, tableData: [], totalExemptions: 0 });
        return;
      }

      if (results.totalExemptions < 1) {
        sweetalert.fire({
          icon: "error",
          title: "",
          text: "No records found",
        });
        this.setState({ showLoading: false, tableData: [], totalExemptions: 0 });
        return;
      }

      let sorter = new Sorter<any>();

      this.setState({
        tableData: slicePages(sorter.sortByKey(
          results.table_data,
          "CreatedDate" as keyof any,
          "desc"
        ), 1, ITEMS_PER_PAGE),
        direction: this.state.direction === "asc" ? "desc" : "asc",
      });

      this.setState({
        totalExemptions: results.totalExemptions.toString(),
        searched: this.state.filteredObj,
        page_options: getPageOptions(results.num_pages),
        showLoading: false,
      });
    });
  }

  modalSubmitted() {
    hideModal(ExemptionRecordModal.ID);
    this.setState({ showLoading: true }, async () => {
      let result = await ExemptionAPI.getFilteredExemptions(1, {
        filter: this.state.filteredObj,
      });
      if (result.totalExemptions < 1) {
        this.setState({ showLoading: false, tableData: [], totalExemptions: 0 });
        return sweetalert.fire({
          title: "",
          html: "<p aria-live='polite'>Unable to filter Results data at this time</p>",
          icon: "error",
        });
      }

      this.setState({
        totalExemptions: result.totalExemptions.toString(),
        showLoading: false,
        page_options: getPageOptions(result.num_pages),
        tableData: result.table_data,
        searched: this.state.filteredObj,
      });
    });
  }

  openPDFNewTab(){
    let sampleService = this.props.services && this.props.selectedSample ? this.props.services.find(f => f.ID === this.props.selectedSample.ServiceID) : null;
    if(!sampleService){
        return sweetalert.fire({icon: 'error', title: '', text: 'Could not create report at this time'})
    }
    let reportType = sampleService.ReportType;
    if(!reportType){
        return sweetalert.fire({icon: 'error', title: '', text: 'Could not create report at this time'})
    }
    if(reportType >= 3){
      this.setState({ showLoadingForReportSelect: true }, async () => {
        let reason;
        try {
            // const response = await SamplesAPI.get6PlexPDFReport(this.props.selectedSample.ID);
            // reason = response && response.reason ? response.reason : '';
            // let data = await response.arrayBuffer();
            // const blob = new Blob([data], {type: 'application/pdf'});
            // const url = URL.createObjectURL(blob)
            // window.open(url, '_blank')
            showModalNoOutsideClick(ReportSelectModal.ID)
            this.setState({showLoadingForReportSelect: false})
        } catch (e) {
            this.setState({showLoadingForReportSelect: false})
            return sweetalert.fire({icon: 'error', title: '', text: reason})
        }
      });
    }
  }

  handleExportToCSV() {
    // Admins use the async Exports panel (background job → download). School users keep
    // the synchronous download — their export is facility-scoped and small.
    if (!this.state.isSchoolUser) {
      showModalNoOutsideClick(ExemptionExportsModal.ID);
      return;
    }

    let filter = this.state.searched;

    sweetalert
      .fire({
        title: "Should your export include Patient Identifiers?",
        showCloseButton: true,
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: "Include",
        denyButtonText: `Don't include`,
      })
      .then((result) => {
        if (result.isConfirmed || result.isDenied) {
          let modifiedfilter = Object.assign(filter, {
            IncludePHI: result.isConfirmed ? true : false,
          });
          this.setState({ showLoading: true }, async () => {
            await NetworkUtil.downloadCSV(
              "/api/admin/exemption/csv",
              "Exemption Records.xlsx",
              { filter: modifiedfilter });
            this.setState({ showLoading: false });
          });
        }
      });
  }

  // School-user-only button: an immediate synchronous download of their school's approved 6th-grade
  // students, with parent contact info, so they can be renewed to 7th grade or have their parents
  // notified. (Admins get this export from the Exports panel instead — it runs as a background job.)
  handleExportExpiring6thGrade() {
    sweetalert
      .fire({
        title: "Export expiring 6th-grade exemptions?",
        html:
          "This downloads your school's approved <strong>6th-grade</strong> exemptions, " +
          "with parent/guardian contact info, so you can renew them for 7th grade or notify parents. " +
          "A vaccine exemption expires after 6th grade.",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Export",
        cancelButtonText: "Cancel",
      })
      .then((result) => {
        if (result.isConfirmed) {
          this.setState({ showLoading: true }, async () => {
            // Send no filter: the school-user export is always the full 6th-grade list for their
            // school (the server ignores page filters for school users). Passing this.state.searched
            // would imply the on-screen filter narrows it, which it doesn't.
            const res = await NetworkUtil.downloadCSV(
              "/api/admin/exemption/expired-export",
              "Expiring 6th Grade Exemptions.xlsx",
              { filter: {} });
            this.setState({ showLoading: false });
            if (res && !res.downloaded) {
              sweetalert.fire({
                icon: "info",
                title: "",
                text: res.reason || "There are no expiring 6th-grade exemptions to export.",
              });
            }
          });
        }
      });
  }

  submit(page) {
    this.setState({ showLoading: true, tableData: [], hasSearched: true }, async () => {
      hideModal(ExemptionRecordModal.ID);
      try {
        let result = await ExemptionAPI.getFilteredExemptions(page, {
          filter: this.state.filteredObj,
        });

        if (result.errorCode === "RATE_LIMIT_EXCEEDED") {
          sweetalert.fire({
            icon: "warning",
            title: "Search Limit Reached",
            text: result.message || "You have exceeded the search limit. Please try again later.",
          });
          this.setState({ showLoading: false, tableData: [], totalExemptions: 0 });
          return;
        }

        if (result.totalExemptions < 1) {
          sweetalert.fire({
            icon: "error",
            title: "",
            html: "<p aria-live='polite'>No records found</p>",
          });
          this.setState({ showLoading: false, tableData: [], totalExemptions: 0 });
          return;
        }
        hideModal(ExemptionRecordModal.ID);

        this.setState({
          totalExemptions: result.totalExemptions.toString(),
          showLoading: false,
          page_options: getPageOptions(result.num_pages),
          tableData: result.table_data,
          searched: this.state.filteredObj,
        });
      } catch (e) {
        console.error(e);
        sweetalert.fire({
          icon: "error",
          title: "",
          text: "Search failed. Please try again.",
        });
        this.setState({ tableData: [], showLoading: false });
      }
    });
  }

  async viewPDFNewTab(path){
    let report;
    try {
        this.setState({showLoadingResultReport:true}, async ()=>{
          if (path === null) {
            let generate = await ExemptionAPI.generateCertificateForReqNum(this.state.selectedExemption.ReqNum);
            if (generate && generate.data) {
              report = await ExemptionAPI.viewCertificate(generate.data);
              let data = await report.arrayBuffer();
              const blob = new Blob([data], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }
          } else {
            report = await ExemptionAPI.viewCertificate(path);
            let data = await report.arrayBuffer();
            const blob = new Blob([data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
          }
          this.setState({showLoadingResultReport:false})
        })
    } catch (error) {
      console.error(error);
      sweetalert.fire({icon: "error", title: "Error", text: `Error opening report for viewing`})
    }
  }

  changePage(page: number) {
    this.setState({ showLoading: true, tableData: [] });
    ExemptionAPI.getFilteredExemptions(page, { filter: this.state.searched })
      .then(result => this.setState({ tableData: result.table_data, showLoading: false }))
      .catch(e => { console.error(e); this.setState({ showLoading: false }); });
  }

  useSorter(col: Column) {

    let sorter = new Sorter<any>();

    this.setState({
      tableData: sorter.sortByKey(
        this.state.tableData,
        col.key as keyof any,
        this.state.direction
      ),
      direction: this.state.direction === "asc" ? "desc" : "asc",
    });
  }

  async handleAcceptDataAgreement() {
    if (!this.state.userUUID) return;
    this.setState({ showLoading: true });
    let response = await UsersAPI.acceptDataAgreement(this.state.userUUID);
    if (!response || !response.success) {
      this.setState({ showLoading: false });
      sweetalert.fire({ icon: 'error', text: 'Unable to save data agreement. Please try again later.' });
      return;
    }
    // Agreement accepted — check if ID verification is still needed
    AdminAPI.getPermissions().then((data) => {
      if (data.isSchoolUser && data.idVerificationStatus !== 'Approved') {
        this.setState({ dataAgreementAccepted: true, idVerificationStatus: data.idVerificationStatus || null, showLoading: false });
        return;
      }
      this.setState({ isAdmin: data.isAdmin, isSchoolUser: data.isSchoolUser, dataAgreementAccepted: true, idVerificationStatus: 'Approved', userFacilityIDs: data.userFacilityIDs || [], userFacilities: data.userFacilities || [], permissionsLoaded: true }, () => {
        this.loadInitialExemptions();
      });
    });
  }

  handleIDUploadComplete() {
    this.setState({ idVerificationStatus: 'Pending' });
  }

  render() {
    // Wait for permissions to load before rendering anything
    if (!this.state.permissionsLoaded) {
      return <Overlay show_loading={true} />;
    }

    // School User onboarding: ID Verification upload (no prior submission)
    if (this.state.isSchoolUser && this.state.dataAgreementAccepted && !this.state.idVerificationStatus) {
      return <IDVerificationUpload userUUID={this.state.userUUID} onUploadComplete={() => this.handleIDUploadComplete()} />;
    }

    // School User onboarding: ID Verification pending admin review
    if (this.state.isSchoolUser && this.state.dataAgreementAccepted && this.state.idVerificationStatus === 'Pending') {
      return <IDVerificationPending />;
    }

    // School User onboarding: ID was denied, allow resubmission
    if (this.state.isSchoolUser && this.state.dataAgreementAccepted && this.state.idVerificationStatus === 'Denied') {
      return <IDVerificationDenied onResubmit={() => this.setState({ idVerificationStatus: null })} />;
    }

    // Show HIPAA Data Agreement if School User hasn't accepted yet
    if (this.state.isSchoolUser && this.state.dataAgreementAccepted === false) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <div style={{
            width: '100%',
            maxWidth: '600px',
            padding: '2rem 2.5rem',
            borderRadius: '5px',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.15)',
            backgroundColor: '#fff',
            textAlign: 'center',
            fontFamily: 'inherit'
          }}>
            {/* Warning icon matching SweetAlert style */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              border: '4px solid #f8bb86',
              margin: '0 auto 1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ fontSize: '2.5rem', color: '#f8bb86', fontWeight: 600, lineHeight: 1 }}>!</span>
            </div>

            <h2 style={{ fontSize: '1.35rem', fontWeight: 600, marginBottom: '1.25rem', color: '#595959' }}>
              OSDH IMM Exemption Portal Individual User Agreement
            </h2>

            <div style={{ textAlign: 'left', maxHeight: '400px', overflowY: 'auto', padding: '0 1rem', fontSize: '0.95rem', color: '#545454' }}>
              <p style={{ marginBottom: '1rem' }}>
                The information in the OSDH IMM Exemption Portal contains data about individuals that is to be treated in a manner so as to preserve the confidentiality of the individuals. As a School User:
              </p>
              <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>I will use the OSDH IMM Exemption Portal only for vaccine exemption verification or to meet vaccine exemption reporting requirements.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>I will safeguard my Username and Password against use by another individual. Sharing of User Name/Password with another individual is NOT ALLOWED and will result in immediate termination from the OSDH IMM Exemption Portal.</strong></li>
                <li style={{ marginBottom: '0.5rem' }}>I will avoid any action that would provide information to others which could identify individuals reported in the OSDH IMM Exemption Portal unless specifically authorized by OSDH Immunization Services.</li>
                <li style={{ marginBottom: '0.5rem' }}>I will <strong>NOT</strong> make copies of an individual's OSDH IMM Exemption Portal record except as authorized by OSDH Immunization Services.</li>
                <li style={{ marginBottom: '0.5rem' }}>I will only discuss information on an OSDH IMM Exemption Portal record as is necessary for medical care/exemption verification and never in a manner or location that would reveal the individual's identity to unauthorized individuals.</li>
              </ol>
              <p style={{ marginBottom: '1rem' }}>
                The information received in the OSDH IMM Exemption Portal is important for patient care, legal, statistical, and research purposes. It is essential that the general public is confident OSDH IMM Exemption Portal school users respect their privacy and maintain confidentiality of the information within the OSDH IMM Exemption Portal. Statutory and regulatory requirements make it essential that users follow the rules outlined above. Your cooperation and assistance in maintaining the confidentiality is vital. A violation of this confidentiality agreement is grounds for disciplinary action, which may result in dismissal from using the OSDH IMM Exemption Portal and/or legal prosecution.
              </p>
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    id="data-agreement-checkbox"
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    onChange={() => { this.setState({ dataAgreementError: false }); }}
                  />
                  <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>I have read and agree to follow the above OSDH IMM Exemption Portal Individual User Agreement</span>
                </label>
              </div>
            </div>

            {this.state.dataAgreementError && (
              <p style={{ color: '#dc3741', fontSize: '0.95rem', fontWeight: 500, marginTop: '1rem', marginBottom: '0' }}>
                You must accept the User Agreement to continue.
              </p>
            )}

            <button
              id="accept-agreement-btn"
              onClick={() => {
                const checkbox = document.getElementById('data-agreement-checkbox') as HTMLInputElement;
                if (!checkbox || !checkbox.checked) {
                  this.setState({ dataAgreementError: true });
                  return;
                }
                this.handleAcceptDataAgreement();
              }}
              style={{
                marginTop: '1.5rem',
                padding: '0.625em 1.1em',
                fontSize: '1rem',
                fontWeight: 500,
                border: 'none',
                borderRadius: '5px',
                backgroundColor: '#21212E',
                color: '#fff',
                cursor: 'pointer',
                minWidth: '120px'
              }}
            >
              Continue
            </button>
          </div>
        </div>
      );
    }

    return (
      <React.Fragment>
        <PaginationTool />
        <Overlay show_loading={this.state.showLoading} />
        <VaccineExemptionCertificate
          data={this.state.selectedExemption}
          exemptImmunizations={this.state.selectedExemption ? JSON.parse(this.state.selectedExemption?.ExemptImmunizations) : null}
          immunizations={this.state.immunizations}
          genders={this.state.genders}
          races={this.state.races}
          branding={this.state.branding}
        />
        <ExemptionRecordModal
          selectedExemption={this.state.selectedExemption}
          immunizations={this.state.immunizations}
          countries={this.state.countries}
          states={this.state.states}
          genders={this.state.genders}
          races={this.state.races}
          ethnicities={this.state.ethnicities}
          onClose={() => this.setState({ selectedExemption: null })}
          onSubmit={() => { this.modalSubmitted() }}
          isAdmin={this.state.isAdmin}
          isSchoolUser={this.state.isSchoolUser}
          userFacilityIDs={this.state.userFacilityIDs}
          userFacilities={this.state.userFacilities}
          openSearchMode={this.state.openSearchMode}
          counties={this.state.counties}
        />
        <ReportSelectModal header="Certificate"
          zIndex={100009}
          reqNum={this.state.selectedExemption?.ReqNum}
          handleInBetweenOverlay={() => { }}
          handleViewPDF={(filePath) => {
            this.viewPDFNewTab(filePath);
          }}
        />

        <ExemptionExportsModal filter={this.state.searched} />

        <div className="container-fluid ">
          <main id="main-content" tabIndex={-1} aria-label="Vaccine Exemptions">
            <div className="card m-2">
              <div className="card-header verlag-bold d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{this.state.isSchoolUser && !this.state.openSearchMode ? "My School's Exemption Records" : 'Exemption Records'}</h3>
                {this.state.isSchoolUser && (
                  <button
                    className={`btn ${this.state.openSearchMode ? 'btn-outline-secondary' : 'btn-primary'}`}
                    onClick={() => {
                      const switchingToOpenSearch = !this.state.openSearchMode;
                      this.setState({
                        openSearchMode: switchingToOpenSearch,
                        openSearchFirstName: '',
                        openSearchLastName: '',
                        openSearchDOB: '',
                        tableData: [],
                        totalExemptions: 0,
                        page_options: [{ label: "1", value: "1" }],
                        selected_page: { label: "1", value: 1 },
                      }, () => {
                        if (!switchingToOpenSearch) {
                          this.loadInitialExemptions();
                        }
                      });
                    }}
                  >
                    {this.state.openSearchMode ? "Back to My School's Records" : 'Search All Schools & Childcares'}
                  </button>
                )}
              </div>

              {this.state.openSearchMode ? (
                <>
                  <div className="card-body">
                    <p className="mb-3" style={{fontSize: '0.9em', color: '#666'}}>
                      Search across all exemption records by entering the child's last name, first name, and date of birth. All three fields are required.
                    </p>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label htmlFor="openSearchLastName" style={{fontWeight: 'bold'}}>Child Last Name</label>
                          <input
                            id="openSearchLastName"
                            className="form-control"
                            type="search"
                            autoComplete="off"
                            value={this.state.openSearchLastName || ''}
                            onChange={(e) => this.setState({ openSearchLastName: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label htmlFor="openSearchFirstName" style={{fontWeight: 'bold'}}>Child First Name</label>
                          <input
                            id="openSearchFirstName"
                            className="form-control"
                            type="search"
                            autoComplete="off"
                            value={this.state.openSearchFirstName || ''}
                            onChange={(e) => this.setState({ openSearchFirstName: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label htmlFor="openSearchDOB" style={{fontWeight: 'bold'}}>Child Date of Birth</label>
                          <input
                            id="openSearchDOB"
                            className="form-control"
                            type="date"
                            max={new Date().toISOString().split('T')[0]}
                            value={this.state.openSearchDOB || ''}
                            onChange={(e) => this.setState({ openSearchDOB: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer">
                    <button
                      className={"btn immySubmitButtonOutline"}
                      onClick={() => {
                        if (!this.state.openSearchFirstName || !this.state.openSearchLastName || !this.state.openSearchDOB) {
                          sweetalert.fire({ icon: 'error', title: '', text: 'Please enter first name, last name, and date of birth.' });
                          return;
                        }
                        this.setState({
                          selected_page: { label: 1, value: 1 },
                          filteredObj: {
                            ChildFirstName: this.state.openSearchFirstName.trim(),
                            ChildLastName: this.state.openSearchLastName.trim(),
                            ChildDOB: moment(this.state.openSearchDOB).format("MM-DD-YYYY"),
                            openSearch: true,
                          }
                        }, () => this.submit(1));
                      }}
                    >
                      Search
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <ExemptionRecordsFilterTabs
                    ref={this.exemptionFilterTabsRef}
                    immunizations={this.state.immunizations}
                    states={this.state.states}
                    counties={this.state.counties}
                    countries={this.state.countries}
                    ethnicity={this.state.ethnicities}
                    race={this.state.races}
                    gender={this.state.genders}
                    exemptionTypes={this.state.exemptionTypes}
                    districts={this.state.districts}
                    schools={this.state.schools?.map((school) => { return { label: school.FacilityName, value: school.ID } })}
                    grades={this.state.grades}
                    reasons={this.state.exemptionReasons}
                    isSchoolUser={this.state.isSchoolUser}
                  />

                  <div className="card-footer">
                    <button
                      className={"btn immySubmitButtonOutline"}
                      onClick={() => {
                        let returnedFilteredObj = this.exemptionFilterTabsRef.current.preSubmitChecks()

                        if (!returnedFilteredObj) {
                          return
                        }
                        else {
                          this.setState({ selected_page: { label: 1, value: 1 }, filteredObj: returnedFilteredObj }, () => this.submit(1))
                        }
                      }}
                    >
                      Search
                    </button>
                  </div>
                </>
              )}
            </div>
          </main>
          <div className="col-12 col-md-12 col-lg-12 pt-2">
            {!this.state.isSchoolUser && !this.state.hasSearched && (!this.state.tableData || this.state.tableData.length === 0) && (
              <div className="card mt-2 mb-5">
                <div className="card-body text-center text-muted py-5">
                  Use the filters above and click <strong>Search</strong> to view exemption records.
                </div>
              </div>
            )}
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="card mt-2 mb-5">
                <div className="card-header verlag-bold stickToTop" style={{ zIndex: 2 }}>
                  <h4 className="text-center text-md-left">
                    Exemptions
                    <button
                      className={
                        this.state.totalExemptions < 1
                          ? "d-none"
                          : "btn btn-outline-primary d-none d-md-inline-block d-xxl-inline-block ml-2"
                      }
                      onClick={() => this.handleExportToCSV()}
                    >
                      {this.state.isSchoolUser ? "Export to CSV" : "Export"}
                    </button>
                    {this.state.isSchoolUser && (
                      <button
                        aria-label="Export Expiring 6th-Grade Exemptions"
                        title="Download your school's approved 6th-grade exemptions, with parent contact info, so they can be renewed for 7th grade. Best run near the end of the school year."
                        className={
                          this.state.totalExemptions < 1
                            ? "d-none"
                            : "btn btn-outline-primary d-none d-md-inline-block d-xxl-inline-block ml-2"
                        }
                        onClick={() => this.handleExportExpiring6thGrade()}
                      >
                        Export Expiring 6th-Grade Exemptions
                      </button>
                    )}
                    <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                      <h4 className={"float-md-right"} role="alert">
                        Total: {this.state.totalExemptions}
                      </h4>
                      <h4 className="d-inline-block float-right align-middle pr-2 ml-5">
                        Page{" "}
                      </h4>
                      <div className=" align-middle float-right pages ">
                        <Select
                          isSearchable={true}
                          placeholder={"1"}
                          noOptionsMessage={() => "No option"}
                          value={this.state.selected_page}
                          aria-label='Table Page Number'
                          onChange={(e: ReactSelect) =>
                            this.setState({ selected_page: e }, () =>
                              this.changePage(e.value)
                            )
                          }
                          className={"state_select"}
                          options={this.state.page_options}
                        />
                      </div>
                    </section>
                  </h4>
                </div>
                <div className="p-0 table-responsive">
                  <SimpleTable
                    columns={[
                      {
                        label: "Confirmation #",
                        key: "ReqNum",
                        popoverText: "Click to Sort By Confirmation #",
                        rawFormat: (val) => {
                          return (
                            <a
                              href={"#"}
                              className={'tableNameLinkColor'}
                              onClick={() => {
                                this.setState({ selectedExemption: val }, () =>
                                  showModalNoOutsideClick(
                                    ExemptionRecordModal.ID
                                  )

                                );
                              }}
                            >
                              {val.ReqNum}
                            </a>
                          );
                        },
                      },
                      {
                        label: "Name",
                        key: "Name",
                        popoverText: "Click to Sort By Name",
                        rawFormat: (val) => {
                          return (
                            <p>
                              {val.ChildMiddleInitial ? val.ChildFirstName + " " + val.ChildMiddleInitial + " " + val.ChildLastName : val.ChildFirstName + " " + val.ChildLastName}
                            </p>
                          );
                        },
                      },
                      {
                        label: "Exemption Type",
                        key: "ExemptionType",
                        popoverText: "Click to Sort By Exemption Type",
                        rawFormat: (val) => {
                          return <p>{getLabel(val.ExemptionType, this.state.exemptionTypes)?.label}</p>;
                        }
                      },
                      {
                        label: "Status",
                        key: "Status",
                        popoverText: "Click to Sort By Status",
                        rawFormat: (val) => {
                          return <p>{val?.Status ? "Approved" : val?.Status == 0 ? "Denied" : "Pending"}</p>;
                        }
                      },
                      {
                        label: "Date/Time of Submission",
                        key: "CreatedDate",
                        popoverText: "Click to Sort By Created Date",
                        rawFormat: (val) => {
                          return <p>{moment(val?.CreatedDate).format("MM-DD-YYYY hh:mm a")}</p>;
                        }
                      },
                      {
                        label: "Certificate", key: "",
                        rawFormat: (val) => (
                          <button
                            type={'button'}
                            className={'btn btn-outline-primary'}
                            style={{ zIndex: 1 }}
                            onClick={() => { this.setState({ selectedExemption: val }, () => showModalNoOutsideClick(ReportSelectModal.ID)) }}
                            disabled={val.Status == null}
                          >
                            {"View Certificate"}
                          </button>)
                      }
                    ]}
                    table_data={this.state.tableData}
                    columnClickedCallback={(col) => {
                      this.useSorter(col);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

        </div>
      </React.Fragment>
    );
  }
}
